import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    Validators
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, throwError } from 'rxjs';
import { catchError, first, switchMap, tap } from 'rxjs/operators';
import { User } from '../../../auth/models';
import { AuthService } from '../../../auth/services';
import { isAPIApplicationException } from '../../../core/helpers';
import { LanguagesService } from '../../../domain/services/languages.service';
import { ChangesNotSavedComponent } from '../../../shared/changes-not-saved';
import { SecurityCodeComponent } from '../../components';

@Component({
    selector: 'app-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./personal-information.component.scss'],
    standalone: false
})
export class PersonalInformationComponent
    extends ChangesNotSavedComponent
    implements OnInit
{
    personalInformation = this._fb.group({
        userId: [undefined, Validators.required],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        emailOrPhoneNumber: [undefined, Validators.required],
        preferredLanguageId: ['', Validators.required]
    });
    submitted = false; // because we are extending ChangesNotSavedComponent
    languages$ = this._languages.languages$;

    constructor(
        private readonly _fb: UntypedFormBuilder,
        private readonly _snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService,
        private readonly _auth: AuthService,
        private readonly _languages: LanguagesService,
        private readonly _dialog: MatDialog
    ) {
        super(); // because we are extending ChangesNotSavedComponent
        document
            .getElementsByTagName('body')[0]
            .classList.remove('blue-background-active');
    }

    ngOnInit(): void {
        this._auth.user$.subscribe((user) =>
            this.personalInformation.patchValue({ ...user })
        );
    }

    get firstName(): AbstractControl | null {
        return this.personalInformation.get('firstName');
    }

    get lastName(): AbstractControl | null {
        return this.personalInformation.get('lastName');
    }

    get emailOrPhoneNumber(): AbstractControl | null {
        return this.personalInformation.get('emailOrPhoneNumber');
    }

    get preferredLanguageId(): AbstractControl | null {
        return this.personalInformation.get('preferredLanguageId');
    }

    onSubmit(event: any): void {
        if (this.personalInformation.valid) {
            if (this.emailOrPhoneNumber?.dirty) {
                let dialog: MatDialogRef<SecurityCodeComponent>;
                this._auth
                    .requestUpdateUserCode(
                        this.personalInformation.get('userId')?.value,
                        this.emailOrPhoneNumber.value
                    )
                    .pipe(
                        catchError((e) => this.handleCodeRequestError(e)),
                        switchMap(() => {
                            dialog = this._dialog.open(SecurityCodeComponent, {
                                maxWidth: 500,
                                minHeight: 250
                            });
                            return dialog.afterClosed();
                        })
                    )
                    .subscribe(() => {
                        if (dialog.componentInstance.complete) {
                            this._auth.setCurrentSecurityCode(
                                dialog.componentInstance.code as number
                            );
                            this.updateUser();
                        }
                    });
            } else {
                this.updateUser();
            }
        }
    }

    isChanged(): boolean {
        return this.personalInformation.touched;
    }

    isSaved(): boolean {
        return this.submitted;
    }

    private updateUser() {
        const updatedUser = new User({
            ...this.personalInformation.value
        });
        this._auth
            .updateUser(updatedUser)
            .pipe(
                switchMap(() =>
                    this._transloco
                        .selectTranslate('application.snack.saved')
                        .pipe(first())
                ),
                tap((message) => {
                    this._snackBar.open(message, undefined, {
                        duration: 4000
                    });
                    this.submitted = true;
                    window.location.reload();
                })
            )
            .subscribe();
    }

    private handleCodeRequestError(e: any): Observable<never> {
        if (isAPIApplicationException(e)) {
            this.emailOrPhoneNumber?.setErrors({
                invalidEmailOrPhoneNumber: true
            });
        }
        return throwError(e);
    }
}
